import { c as _c } from "react/compiler-runtime";
import { Drawer } from '@base-ui/react/drawer';
import { Badge, BottomDrawer, Button, Popover, TooltipTextOverflow } from '@systemeio/components';
import { FilterLinesIcon, LayoutRightIcon, RefreshCw01Icon, XCloseIcon } from '@systemeio/icons';
import { createContext, Fragment, useCallback, useContext, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { FormControlSizeProvider } from 'shared/components/form-control-size-context';
import { SideDrawer } from 'shared/components/side-drawer';
import { ViewportSwitch } from 'shared/components/viewport-switch';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useMediaQuery from 'shared/hooks/use-media-query';
import { twJoin, twMerge } from 'tailwind-merge';

/** Field width where a section sets no `--filter-field-width`. Mobile never reaches it. */
const FIELD_WIDTH = {
  desktop: 280,
  tablet: 260
};

/**
 * Gap between the measured parts, in px. Inline rather than a `gap-*` class so the value that
 * draws the space is the same one the fits/does-not-fit math subtracts; a consumer's `className`
 * must not override it. Rows the math does not measure keep the ordinary `gap-3`.
 */
const GAP = 12;
export interface HeaderFilterControl {
  key: React.Key;
  node: ReactNode;
}
export interface HeaderFilterGroup {
  controls: HeaderFilterControl[];
  activeCount: number;
  dirty: boolean;
  canApply: boolean;
  clear: () => void;
  apply: () => boolean;
  setInline: (inline: boolean) => void;
  discard: () => void;
  /** Always collapsed: a side drawer on desktop/tablet, a bottom drawer on mobile. */
  complex?: boolean;
  /**
   * Everything below the panel's own title, composed out of `FilterPanel.*` — the only way
   * section-specific content reaches the popover or drawer. Left out, the header renders the
   * controls and, in a drawer, a footer with Clear all and Apply.
   */
  panel?: ReactNode;
}
type FilterPanelShell = 'popover' | 'side-drawer' | 'bottom-drawer';
interface FilterPanelContextValue {
  shell: FilterPanelShell;
  /** Left out while the panel is closed: only the open container may hold the controls host. */
  slotRef?: (slot: HTMLDivElement | null) => void;
  clear: ReactNode;
  apply: ReactNode;
}
const FilterPanelContext = createContext<FilterPanelContextValue | undefined>(undefined);
function useFilterPanel(part) {
  const context = useContext(FilterPanelContext);
  if (!context) {
    throw new Error(`FilterPanel.${part} is only valid inside a filter group's \`panel\`.`);
  }
  return context;
}
export interface FilterHeaderProps {
  leadingContent?: ReactNode;
  title?: ReactNode;
  counter?: ReactNode;
  help?: ReactNode;
  filters?: HeaderFilterGroup;
  /**
   * One entry per visible Action — a dropdown with several items is one entry, a falsy entry is an
   * Action the page hides. §2 forbids inferring them from the JSX, and their number decides
   * placement, so the array is the classification.
   */
  actions?: ReactNode[];
  className?: string;
}
export function FilterHeader({
  title,
  leadingContent,
  counter,
  help,
  filters,
  actions,
  className
}: FilterHeaderProps) {
  const {
    t
  } = useLocoTranslation();
  const visibleActions = (actions ?? []).filter(Boolean);
  const actionCount = visibleActions.length;
  const {
    isMobile,
    isDesktop
  } = useMediaQuery();
  const [open, setOpen] = useState(false);
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  const [widths, setWidths] = useState({
    container: null as number | null,
    title: 0,
    actions: 0,
    clear: 0,
    mobile: 0,
    field: 0
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const mobileActionsRef = useRef<HTMLDivElement>(null);
  const desktopActionsRef = useRef<HTMLDivElement>(null);
  const actionsRef = isMobile ? mobileActionsRef : desktopActionsRef;
  const clearRef = useRef<HTMLButtonElement>(null);
  const controlsRef = useRef<HTMLDivElement>(null);
  const movingFocus = useRef<HTMLElement | null>(null);
  const focusedControl = useRef<HTMLElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const fixedActions = isMobile && actionCount >= 2;
  const headerActions = fixedActions ? 0 : actionCount;
  const count = filters?.controls.length ?? 0;
  // A complex group is an editor behind one control, never one logical filter, so it keeps the
  // numeric badge and Clear all instead of the single-filter dot.
  const singleFilter = count === 1 && !filters?.complex;
  const singleMobileFilter = isMobile && singleFilter;
  const showBadge = !!filters?.activeCount && !singleMobileFilter;
  const layoutPending = widths.container === null && !filters?.complex;
  const containerWidth = widths.container ?? 0;
  const fieldWidth = widths.field || (isDesktop ? FIELD_WIDTH.desktop : FIELD_WIDTH.tablet);
  const rightAligned = !isMobile && !filters?.complex && count === 1 && actionCount === 0 && fieldWidth + widths.title + GAP <= containerWidth;
  const groupWidth = count * fieldWidth + Math.max(0, count - 1) * GAP + (count > 1 ? widths.clear + GAP : 0);
  const collapsed = !!filters && (filters.complex || (isMobile ?
  // A lone control owns the whole second row, so it always fits and only an Action can
  // collapse it; from two controls up their natural widths decide.
  headerActions > 0 || count > 1 && widths.mobile > containerWidth : groupWidth > containerWidth));
  const secondRow = isMobile || !collapsed &&
  // Actions add a second gap to the row: Title · group · Actions, against Title · group alone.
  groupWidth + widths.title + widths.actions + (actionCount ? GAP * 2 : GAP) > containerWidth;
  const panelOpen = collapsed && open;
  const measuring = isMobile && collapsed && !panelOpen && !filters?.complex;
  const stackedControls = collapsed && !measuring;
  /*
   * Inline — and in the second row — the group shares the header's own line, so its fields are the
   * compact 32px control. An open overlay (popover, side drawer, bottom drawer) is a surface of its
   * own with the room for the ordinary 40px field, and the panel's own Clear / Apply follow the
   * fields beside them. The off-screen copy stays compact deliberately: it measures the width the
   * group would need if it went back inline, which is a 32px row.
   */
  const controlSize = panelOpen ? 'md' : 'sm';
  const drawer = isMobile || filters?.complex;
  const Trigger = drawer ? Drawer.Trigger : Popover.Trigger;
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && isMobile) filters?.discard();
    setOpen(nextOpen);
  };
  useLayoutEffect(() => {
    if (!layoutPending) filters?.setInline(!collapsed);
  }, [collapsed, layoutPending, filters?.setInline]);
  useLayoutEffect(() => {
    setHost(document.createElement('div'));
  }, []);
  useLayoutEffect(() => {
    const measure = () => setWidths(previous => {
      const next = {
        field: containerRef.current ? Number.parseFloat(getComputedStyle(containerRef.current).getPropertyValue('--filter-field-width')) || 0 : 0,
        container: containerRef.current?.clientWidth ?? 0,
        title: titleRef.current?.scrollWidth ?? 0,
        actions: fixedActions ? 0 : actionsRef.current?.scrollWidth ?? 0,
        clear: clearRef.current?.getBoundingClientRect().width ?? 0,
        // The row's own overflow, never the sum of its children: inline the controls stretch to
        // fill the row, so their rounded widths exceed the container and collapse a group that
        // fits, while the off-screen copy reports the natural width and expands it again.
        mobile: isMobile && !panelOpen && controlsRef.current ? controlsRef.current.scrollWidth : previous.mobile
      };
      return Object.keys(next).every(key => next[key as keyof typeof next] === previous[key as keyof typeof next]) ? previous : next;
    });
    measure();
    const observer = new ResizeObserver(measure);
    for (const ref of [containerRef, titleRef, actionsRef, clearRef, controlsRef]) if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [fixedActions, title, actions, count, host, isMobile, isDesktop, panelOpen, measuring, className]);
  useLayoutEffect(() => {
    const parent = containerRef.current?.parentElement;
    if (!fixedActions || !parent) return;
    const previous_0 = parent.style.paddingBottom;
    const update = () => {
      // The guard only keeps a stale zero from removing the space the bar still occupies.
      const height = actionsRef.current?.offsetHeight;
      if (height) parent.style.paddingBottom = `${height}px`;
    };
    update();
    const observer_0 = new ResizeObserver(update);
    if (actionsRef.current) observer_0.observe(actionsRef.current);
    return () => {
      observer_0.disconnect();
      parent.style.paddingBottom = previous_0;
    };
  }, [fixedActions]);

  // Move one portal host between slots; controls and their local input state stay mounted.
  const attach = useCallback((slot: HTMLDivElement | null) => {
    if (!host) return;
    if (!slot) {
      movingFocus.current = host.contains(document.activeElement) ? document.activeElement as HTMLElement : null;
      return;
    }
    // A second `FilterPanel.Controls` steals the host and leaves the first slot empty. The
    // losing slot is still in the document, which is what tells this apart from an ordinary move.
    if (process.env.NODE_ENV !== 'production' && host.parentElement !== slot && host.parentElement?.isConnected) console.warn('FilterHeader: a panel renders the controls slot more than once.');
    slot.appendChild(host);
    if (movingFocus.current) {
      if (slot.inert) triggerRef.current?.focus({
        preventScroll: true
      });else movingFocus.current.focus({
        preventScroll: true
      });
      movingFocus.current = null;
    }
  }, [host]);
  const initialFocus = () => focusedControl.current ?? controlsRef.current?.querySelector<HTMLElement>('input, button') ?? false;
  const restoreFocus = () => triggerRef.current ?? focusedControl.current ?? false;
  const clearButton = filters && !singleFilter && (!isMobile || collapsed) && <Button type="button" variant={collapsed ? 'ghost-gray' : 'ghost-color'} size={panelOpen ? 'md' : 'sm'} onClick={filters.clear} disabled={!filters.dirty} className={twJoin('shrink-0 whitespace-nowrap', collapsed && !drawer && 'self-end', !isMobile && !collapsed && !filters.dirty && 'invisible')} tabIndex={filters.dirty ? undefined : -1} aria-hidden={!isMobile && !collapsed && !filters.dirty}>
      <RefreshCw01Icon aria-hidden />
      {t('dashboard.contacts.filter.clear_all')}
    </Button>;
  const applyButton = isMobile && collapsed && filters && <Button type="button" variant="primary" size={panelOpen ? 'md' : 'sm'} disabled={!filters.canApply} className="shrink-0" onClick={() => {
    // Accepting the draft closes the panel; only dismissal discards it.
    if (filters.apply()) setOpen(false);
  }}>
      {t('global.apply')}
    </Button>;
  const trigger = <div className={twJoin('isolate inline-flex shrink-0 items-center', layoutPending && 'invisible')}>
      <Trigger ref={triggerRef} render={<Button type="button" variant="secondary" size={isMobile && !showBadge ? 'icon-sm' : 'sm'} className={twJoin('gap-1 focus-visible:z-10', !!filters?.activeCount && 'rounded-r-none')} />} aria-label={t('global.filter')}>
        <span className="relative inline-flex">
          <FilterLinesIcon />
          {singleMobileFilter && !!filters?.activeCount && <span aria-hidden className="bg-bg-accent-active ring-bg-primary absolute -top-0.5 -right-0.5 size-1.5 rounded-full ring-2" />}
        </span>
        {!isMobile && <span>{t('global.filter')}</span>}
        {showBadge && <Badge size="sm" shape="rounded" color="blue">
            {filters?.activeCount}
          </Badge>}
      </Trigger>
      {!!filters?.activeCount && <Button type="button" variant="secondary" size="icon-sm" className="-ml-px rounded-l-none focus-visible:z-10" aria-label={t('dashboard.contacts.filter.clear_all')} onClick={filters.clear}>
          <XCloseIcon />
        </Button>}
    </div>;
  const closeButton = (icon: ReactNode) => <Button type="button" variant="ghost-gray" size="icon-sm" aria-label={t('global.close')} onClick={() => handleOpenChange(false)}>
      {icon}
    </Button>;
  const renderedActions = visibleActions.map((action, index) => <Fragment key={index}>{action}</Fragment>);
  const shell: FilterPanelShell = drawer ? isMobile ? 'bottom-drawer' : 'side-drawer' : 'popover';
  const panelBody = <FilterPanelContext.Provider value={{
    shell,
    slotRef: panelOpen ? attach : undefined,
    clear: clearButton,
    apply: applyButton
  }}>
      {filters?.panel ?? <>
          <FilterPanel.Content>
            <FilterPanel.Controls />
          </FilterPanel.Content>
          {/* The popover keeps Clear all at the end of the control row instead (§6). */}
          {drawer && (clearButton || applyButton) && <FilterPanel.Footer>
              {clearButton}
              {applyButton}
            </FilterPanel.Footer>}
        </>}
    </FilterPanelContext.Provider>;
  return <Popover.Root open={panelOpen && !drawer} onOpenChange={handleOpenChange}>
      <Drawer.Root open={panelOpen && drawer} onOpenChange={handleOpenChange} swipeDirection={isMobile ? 'down' : 'right'}>
        <div ref={containerRef} className={twMerge('relative flex min-w-0 flex-wrap items-center', className)} style={{
        gap: GAP
      }} data-filter-layout={collapsed ? 'collapsed' : secondRow ? 'row' : 'inline'}>
          <div ref={titleRef} className="tablet:flex-none flex min-w-0 flex-1 items-center gap-1">
            {leadingContent}
            {title && <div className="text-text-primary min-w-0 text-2xl font-semibold [&_[data-hint-trigger]_svg]:size-5">
                {isMobile ? <TooltipTextOverflow>{title}</TooltipTextOverflow> : <div className="whitespace-nowrap">{title}</div>}
              </div>}
            {counter ? <div className="shrink-0">{counter}</div> : null}
            {help && <div className="shrink-0">{help}</div>}
          </div>
          {count > 1 && !isMobile && <Button ref={clearRef} type="button" variant="ghost-color" size="sm" tabIndex={-1} aria-hidden className="pointer-events-none invisible absolute whitespace-nowrap">
              <RefreshCw01Icon aria-hidden />
              {t('dashboard.contacts.filter.clear_all')}
            </Button>}
          {collapsed && actionCount !== 1 && <>{trigger}</>}
          {filters && !collapsed && <div ref={attach} className={twJoin('min-h-8 min-w-0', layoutPending && 'invisible', secondRow ? 'order-3 w-full' : rightAligned ? 'ml-auto' : '')} />}
          {actionCount > 0 && <ViewportSwitch at="tablet" below={<div className={twJoin(actionCount === 1 ? 'order-3 flex w-full min-w-0 items-center gap-3' : 'contents')}>
                  {collapsed && actionCount === 1 && trigger}
                  <div ref={mobileActionsRef} className={twJoin('flex min-w-0 items-center gap-3 [&>*]:h-auto [&>*]:min-h-8 [&>*]:min-w-0 [&>*]:flex-1 [&>*]:whitespace-normal', actionCount === 1 ? 'flex-1' : 'bg-bg-primary border-border-100 fixed inset-x-0 bottom-0 z-40 flex-wrap border-t px-4 pt-3 pb-[max(12px,env(safe-area-inset-bottom))] [&>*]:basis-auto')}>
                    {renderedActions}
                  </div>
                </div>} from={<>
                  {collapsed && actionCount === 1 && trigger}
                  <div ref={desktopActionsRef} className="ml-auto flex shrink-0 items-center gap-3">
                    {renderedActions}
                  </div>
                </>} />}
        </div>
        {collapsed && !panelOpen && <div ref={attach} aria-hidden inert className="pointer-events-none invisible fixed top-0 -left-[10000px] w-max" />}
        {!drawer && <Popover.Portal>
            <Popover.Positioner anchor={triggerRef} align="start" side="bottom">
              <Popover.Popup initialFocus={initialFocus} finalFocus={restoreFocus} className="w-90 max-w-[min(360px,var(--available-width))] p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <Popover.Title>{t('global.filter')}</Popover.Title>
                  {closeButton(<XCloseIcon />)}
                </div>
                {panelBody}
              </Popover.Popup>
            </Popover.Positioner>
          </Popover.Portal>}
        {drawer && (isMobile ? <BottomDrawer.Popup initialFocus={initialFocus} finalFocus={restoreFocus}>
              <BottomDrawer.Header className="flex-row items-center justify-between gap-3">
                <BottomDrawer.Title>{t('global.filter')}</BottomDrawer.Title>
                {singleMobileFilter && filters && <Button type="button" variant="ghost-color" size="sm" onClick={() => {
            filters.clear();
            handleOpenChange(false);
          }}>
                    {t('global.reset')}
                  </Button>}
              </BottomDrawer.Header>
              {panelBody}
            </BottomDrawer.Popup> : <SideDrawer side="right" onClose={() => handleOpenChange(false)} initialFocus={initialFocus} finalFocus={restoreFocus} className="bg-bg-primary w-full max-w-105">
              <div className="border-border-100 flex shrink-0 items-center justify-between gap-2.5 border-b px-6 py-4">
                <Drawer.Title className="text-text-primary text-xl font-semibold">
                  {t('global.filter')}
                </Drawer.Title>
                {closeButton(<LayoutRightIcon />)}
              </div>
              {panelBody}
            </SideDrawer>)}
        {host && filters && createPortal(<FormControlSizeProvider size={controlSize}>
              <div ref={controlsRef} onFocusCapture={event => {
          focusedControl.current = event.target as HTMLElement;
        }} className={twJoin('flex', stackedControls ? 'flex-col' : 'items-center')} style={{
          gap: GAP
        }} onKeyDown={event_0 => {
          const input = event_0.target;
          if (event_0.key !== 'Enter' || event_0.defaultPrevented || event_0.nativeEvent.isComposing || !(input instanceof HTMLInputElement) || input.getAttribute('role') === 'combobox' || !['text', 'search', 'email', 'number', 'tel', 'url'].includes(input.type)) return;
          event_0.preventDefault();
          if (!isMobile || !collapsed) filters.apply();
        }}>
                {filters.controls.map(control => <div key={control.key} style={!collapsed && !isMobile ? {
            width: fieldWidth
          } : isMobile && !stackedControls && count > 1 ? {
            minWidth: 'max-content'
          } : undefined} className={twJoin('min-w-0 [&>*]:w-full [&>*]:max-w-full [&>*]:min-w-0', stackedControls ? 'w-full' : isMobile ? 'flex-1' : 'shrink-0')}>
                    {control.node}
                  </div>)}
                {!drawer && !(collapsed && filters?.panel) && clearButton}
              </div>
            </FormControlSizeProvider>, host)}
      </Drawer.Root>
    </Popover.Root>;
}

/** A band that keeps the container's horizontal padding and does not scroll with the content. */
function FilterPanelSection(t0) {
  const $ = _c(7);
  const {
    children,
    className
  } = t0;
  const {
    shell
  } = useFilterPanel("Section");
  const t1 = shell === "side-drawer" && "px-6";
  const t2 = shell === "bottom-drawer" && "px-4";
  let t3;
  if ($[0] !== className || $[1] !== t1 || $[2] !== t2) {
    t3 = twMerge("shrink-0", t1, t2, className);
    $[0] = className;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== children || $[5] !== t3) {
    t4 = <div className={t3}>{children}</div>;
    $[4] = children;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}

/** The scrollable area; insert FilterPanel.Controls explicitly to show the controls. */
function FilterPanelContent(t0) {
  const $ = _c(7);
  const {
    children
  } = t0;
  const {
    shell
  } = useFilterPanel("Content");
  if (shell === "bottom-drawer") {
    let t1;
    if ($[0] !== children) {
      t1 = <BottomDrawer.Content>{children}</BottomDrawer.Content>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  const t1 = shell === "popover" ? "max-h-[min(70dvh,var(--available-height))]" : "min-h-0 flex-1 px-6 py-4";
  let t2;
  if ($[2] !== t1) {
    t2 = twJoin("overflow-y-auto", t1);
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children || $[5] !== t2) {
    t3 = <div className={t2}>{children}</div>;
    $[4] = children;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}

/** The slot the single portal host moves into. One panel renders it at most once. */
function FilterPanelControls() {
  const $ = _c(2);
  const {
    slotRef
  } = useFilterPanel("Controls");
  let t0;
  if ($[0] !== slotRef) {
    t0 = <div ref={slotRef} />;
    $[0] = slotRef;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}

/** The footer of the panel, outside its scrollable area. */
function FilterPanelFooter(t0) {
  const $ = _c(7);
  const {
    children
  } = t0;
  const {
    shell
  } = useFilterPanel("Footer");
  if (shell === "bottom-drawer") {
    let t1;
    if ($[0] !== children) {
      t1 = <BottomDrawer.Footer className="gap-3 [&>*]:min-w-0 [&>*]:flex-1">{children}</BottomDrawer.Footer>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  const t1 = shell === "side-drawer" ? "border-border-100 border-t px-6 py-4" : "pt-3";
  let t2;
  if ($[2] !== t1) {
    t2 = twMerge("flex shrink-0 items-center justify-between gap-3", t1);
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children || $[5] !== t2) {
    t3 = <div className={t2}>{children}</div>;
    $[4] = children;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}

/** Renders nothing where §6 gives the group no Clear all. */
function FilterPanelClear() {
  const $ = _c(2);
  const {
    clear
  } = useFilterPanel("Clear");
  let t0;
  if ($[0] !== clear) {
    t0 = <>{clear}</>;
    $[0] = clear;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}

/** Renders nothing outside a collapsed mobile group. */
function FilterPanelApply() {
  const $ = _c(2);
  const {
    apply
  } = useFilterPanel("Apply");
  let t0;
  if ($[0] !== apply) {
    t0 = <>{apply}</>;
    $[0] = apply;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}

/**
 * The parts a group's `panel` is composed of. Each one carries the chrome of the container it
 * lands in, so a section orders its panel without knowing which container opened.
 */
export const FilterPanel = {
  Section: FilterPanelSection,
  Content: FilterPanelContent,
  Controls: FilterPanelControls,
  Footer: FilterPanelFooter,
  Clear: FilterPanelClear,
  Apply: FilterPanelApply
};